"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import categories from "@/data/categories.json";
import {
  Piano,
  Guitar,
  Music,
  Drum,
  Headphones,
  Cpu,
  Package,
  Music2,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/Button";

const getCategoryIcon = (categoryId: string) => {
  const iconMap: Record<string, any> = {
    keyboard: Piano,
    strings: Guitar,
    winds: Music,
    percussion: Drum,
    traditional: Music2,
    audio: Headphones,
    other: Package,
  };
  return iconMap[categoryId] || Music;
};

export default function CategoryPageContent() {
  const router = useRouter();
  const [selectedCategory, setSelectedCategory] = useState<any>(null);

  const handleCategoryClick = (category: any) => {
    router.push(`/list?category=${encodeURIComponent(category.id)}`);
  };

  return (
    <div className="min-h-screen bg-[#F9F8FC] pb-24">
      {/* 헤더 */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-screen-sm mx-auto px-4 py-4">
          <h1 className="text-2xl font-bold text-gray-900">카테고리</h1>
          <p className="text-sm text-gray-600 mt-1">
            원하는 악기 카테고리를 선택하세요
          </p>
        </div>
      </div>

      {/* 카테고리 그리드 */}
      <div className="max-w-screen-sm mx-auto px-4 py-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {categories.categories.map((category, index) => {
            const IconComponent = getCategoryIcon(category.id);
            return (
              <motion.button
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleCategoryClick(category)}
                className="bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-[#7A56E8] hover:shadow-lg transition-all duration-200 text-left group"
              >
                <div className="flex flex-col items-center space-y-3">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-[#7A56E8]/10 to-[#9B82FF]/10 group-hover:from-[#7A56E8]/20 group-hover:to-[#9B82FF]/20 transition-all">
                    <IconComponent className="w-8 h-8 text-[#7A56E8]" />
                  </div>
                  <div className="text-center w-full">
                    <h3 className="font-bold text-gray-900 text-base mb-1">
                      {category.name}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {Array.isArray(category.items)
                        ? `${category.items.length}개 항목`
                        : "상품 보기"}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#7A56E8] transition-colors" />
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* SEO를 위한 카테고리 설명 */}
        <div className="mt-12 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            ToneMarket 카테고리 안내
          </h2>
          <div className="space-y-4 text-gray-700 text-sm leading-relaxed">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">건반악기</h3>
              <p>
                피아노, 디지털 피아노, 전자 키보드, 신시사이저 등 건반이 있는
                모든 악기를 찾아보세요.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">현악기</h3>
              <p>
                기타, 바이올린, 첼로, 우쿨렐레 등 현을 사용하는 모든 악기를
                찾아보세요.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">관악기</h3>
              <p>
                플루트, 클라리넷, 색소폰, 트럼펫 등 관을 통해 소리를 내는 모든
                악기를 찾아보세요.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">타악기</h3>
              <p>
                드럼, 심벌즈, 팀파니 등 두드려서 소리를 내는 모든 악기를
                찾아보세요.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">국악기</h3>
              <p>
                가야금, 거문고, 아쟁, 해금 등 전통 국악기를 찾아보세요.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">음향장비</h3>
              <p>
                마이크, 스피커, 오디오인터페이스, 믹서 등 음향 관련 장비를
                찾아보세요.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

